import { useCallback, useEffect, useRef } from 'react';

interface DebouncedSignalOptions {
  delayMs?: number;
  // Send what is already queued when the tab is hidden or the page is swiped away, which
  // never unmounts the route. Callers keep values still being typed out of the queue.
  flushOnHide?: boolean;
}

export function useDebouncedSignal(
  callback: () => void,
  options: DebouncedSignalOptions = {}
) {
  const { delayMs = 600, flushOnHide = false } = options;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const signal = useCallback(() => {
    cancel();
    timeoutRef.current = setTimeout(() => {
      callbackRef.current();
    }, delayMs);
  }, [cancel, delayMs]);

  const flush = useCallback(() => {
    cancel();
    callbackRef.current();
  }, [cancel]);

  useEffect(() => cancel, [cancel]);

  useEffect(() => {
    if (!flushOnHide) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const onPageHide = () => flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [flushOnHide, flush]);

  return { signal, flush, cancel };
}
